import { api } from "../config/api";
import { post, postAllowHttpError } from "../api";
import type { UsersSessionArgs } from "./users.service";
import type { User } from "../components/MainContent/pages/Users/types";
import type {
  MyProfilePhoneItem,
  MyProfileAddressItem,
  MyProfileIdProof,
  MyProfileUserDetails,
  LoginActivityItem,
} from "../components/MainContent/pages/MyProfile/types";
import { parseIdProofDocumentPath } from "../components/MainContent/pages/MyProfile/utils/parseIdProofDocumentPath";
import { DISPLAY_PLACEHOLDER, formatPhoneForDisplay } from "../utils/formatting";
import { deriveLastActiveFromLoginActivity } from "../components/MainContent/pages/Users/utils";

export interface TeamMemberProfileArgs extends UsersSessionArgs {
  user_id: string | number;
}

interface TeamMemberApiResponse<T = unknown> {
  success?: boolean;
  message?: string;
  data?: T;
  errors?: string | string[];
}

function teamMemberBody(
  session: UsersSessionArgs,
  userId: string | number
): Record<string, unknown> {
  const { business_id, device_token, session_id } = session;
  const uid = typeof userId === "number" ? userId : Number(String(userId).trim());
  return {
    business_id:
      business_id !== null && business_id !== undefined ? Number(business_id) : business_id,
    user_id: Number.isFinite(uid) ? uid : userId,
    device_token,
    session_id,
  };
}

function ensureArray<T>(val: unknown): T[] {
  return Array.isArray(val) ? (val as T[]) : [];
}

function normalizeCountryCode(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) return undefined;
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return undefined;
  return s;
}

function mapPhoneItem(raw: Record<string, unknown>): MyProfilePhoneItem {
  return {
    phone_id:
      (raw.phone_id as string | number | undefined) ??
      (raw.phone_number_id as string | number | undefined) ??
      (raw.id as string | number | undefined),
    phone: String(raw.phone ?? raw.phone_number ?? raw.contact_number ?? "").trim() || undefined,
    country_code: normalizeCountryCode(raw.country_code ?? raw.countryCode),
    phone_type:
      String(raw.phone_type ?? raw.contact_type ?? raw.phone_type_name ?? "").trim() || undefined,
    is_active: raw.is_active !== false,
  };
}

function parseTeamMemberPhones(data: unknown): MyProfilePhoneItem[] {
  if (!data || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  const candidates = [
    ...ensureArray<Record<string, unknown>>(o.phone_number_list),
    ...ensureArray<Record<string, unknown>>(o.phonenumbers),
    ...ensureArray<Record<string, unknown>>(o.phone_numbers),
    ...ensureArray<Record<string, unknown>>(o.phones),
  ];
  if (candidates.length > 0) {
    return candidates.map(mapPhoneItem).filter((p) => p.phone);
  }
  if (Array.isArray(data)) {
    return (data as Record<string, unknown>[]).map(mapPhoneItem).filter((p) => p.phone);
  }
  const single = mapPhoneItem(o);
  return single.phone ? [single] : [];
}

function mapAddressItem(raw: Record<string, unknown>): MyProfileAddressItem {
  const address = String(raw.address ?? raw.full_address ?? "").trim();
  return {
    address_id:
      (raw.address_id as string | number | undefined) ?? (raw.id as string | number | undefined),
    address,
    full_address: address,
    address_type: String(raw.address_type ?? "").trim() || undefined,
    is_active: raw.is_active !== false,
  };
}

function parseTeamMemberAddresses(data: unknown): MyProfileAddressItem[] {
  if (Array.isArray(data)) {
    return (data as Record<string, unknown>[]).map(mapAddressItem).filter((a) => a.address);
  }
  if (!data || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  const list = [
    ...ensureArray<Record<string, unknown>>(o.addresses),
    ...ensureArray<Record<string, unknown>>(o.address_list),
  ];
  return list.map(mapAddressItem).filter((a) => a.address);
}

function stringField(raw: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

function mapProofItem(raw: Record<string, unknown>): MyProfileIdProof {
  const document = String(raw.document ?? raw.file_key ?? "").trim() || undefined;
  const parsedPath = parseIdProofDocumentPath(document);
  const rawDocId = raw.proof_id ?? raw.document_id ?? raw.id;
  const numericId =
    typeof rawDocId === "number"
      ? rawDocId
      : typeof rawDocId === "string" && /^\d+$/.test(rawDocId.trim())
        ? Number(rawDocId.trim())
        : rawDocId;
  const uploadedBy =
    stringField(raw, "uploaded_by", "uploaded_by_name", "created_by_name", "created_by") ??
    stringField(raw, "uploadedBy");
  const uploadedOn =
    stringField(raw, "uploaded_on", "uploaded_at", "created_on", "uploadedOn") ??
    parsedPath?.uploadedOnIsoDate;
  return {
    id: numericId as number | undefined,
    document_id: numericId as number | undefined,
    idproof: document,
    file_key: document,
    doc_name: String(raw.name ?? raw.doc_name ?? raw.filename ?? "").trim() || undefined,
    proof_type: String(raw.proof_type ?? "").trim() || undefined,
    uploaded_by: uploadedBy,
    uploaded_on: uploadedOn,
  };
}

function displayNameFromTeamMember(member: User | null | undefined): string {
  if (!member) return "";
  const fromParts = [member.first_name, member.middle_name, member.surname]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromParts) return fromParts;
  const fromName = (member.name ?? "").trim();
  if (fromName) return fromName;
  return (member.email ?? member.username ?? "").trim();
}

/** Fills uploaded_by when the API omits it but the storage path belongs to the profile subject. */
export function enrichTeamMemberIdProofs(
  proofs: MyProfileIdProof[],
  member: User | null | undefined
): MyProfileIdProof[] {
  const profileUserId =
    member?.user_id !== null && member?.user_id !== undefined ? String(member.user_id).trim() : "";
  const displayName = displayNameFromTeamMember(member);
  if (!profileUserId || !displayName) return proofs;

  return proofs.map((proof) => {
    const existing = proof.uploaded_by?.trim();
    if (existing) {
      if (/^\d+$/.test(existing) && existing === profileUserId) {
        return { ...proof, uploaded_by: displayName };
      }
      return proof;
    }
    const parsed = parseIdProofDocumentPath(proof.file_key ?? proof.idproof);
    if (parsed && String(parsed.userId) === profileUserId) {
      return { ...proof, uploaded_by: displayName };
    }
    return proof;
  });
}

function parseTeamMemberProofs(data: unknown): MyProfileIdProof[] {
  if (Array.isArray(data)) {
    return (data as Record<string, unknown>[]).map(mapProofItem);
  }
  if (!data || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  const list = ensureArray<Record<string, unknown>>(o.idproofs ?? o.proofs);
  return list.map(mapProofItem);
}

export function teamMemberPhonesFromUserDetails(
  details: MyProfileUserDetails | null | undefined
): MyProfilePhoneItem[] {
  if (!details) return [];
  return parseTeamMemberPhones(details);
}

export function formatTeamMemberPhonesForDisplay(phones: MyProfilePhoneItem[]): string {
  const active = phones.filter((p) => p.is_active !== false && p.phone?.trim());
  if (active.length === 0) return DISPLAY_PLACEHOLDER;
  const sorted = [...active].sort((a, b) => {
    const aPrimary = (a.phone_type ?? "").toLowerCase() === "primary" ? 0 : 1;
    const bPrimary = (b.phone_type ?? "").toLowerCase() === "primary" ? 0 : 1;
    return aPrimary - bPrimary;
  });
  return sorted.map((p) => formatPhoneForDisplay(p.country_code, p.phone)).join(", ");
}

export function mapTeamMemberDetailToUser(details: MyProfileUserDetails): User {
  const business = details.business;
  const roleInfo = details.user_permissions?.role_info;
  const roleLabel =
    business?.business_role_shortcode?.trim() ||
    roleInfo?.role_shortcode?.trim() ||
    business?.business_role?.trim() ||
    roleInfo?.role_name?.trim() ||
    details.role?.trim() ||
    "team-member";

  return {
    user_id: String(details.user_id ?? ""),
    first_name: details.first_name,
    middle_name: details.middle_name,
    surname: details.surname,
    username: details.username,
    email: details.email,
    role: roleLabel,
    is_active: details.is_active ?? false,
    last_active: deriveLastActiveFromLoginActivity(details.user_management),
  };
}

async function getTeamMemberDetail(
  args: TeamMemberProfileArgs
): Promise<TeamMemberApiResponse<{ user_details?: MyProfileUserDetails }>> {
  const { business_id, device_token, session_id, user_id } = args;
  if (!business_id || !device_token || !session_id || user_id === "") {
    return { success: false, errors: "Session and user_id required" };
  }
  return post<TeamMemberApiResponse<{ user_details?: MyProfileUserDetails }>>(
    api.GET_TEAM_MEMBER_DETAIL,
    teamMemberBody(args, user_id)
  );
}

async function getTeamMemberPhoneNumbers(
  args: TeamMemberProfileArgs
): Promise<MyProfilePhoneItem[]> {
  const { business_id, device_token, session_id, user_id } = args;
  if (!business_id || !device_token || !session_id || user_id === "") return [];
  const res = await postAllowHttpError<TeamMemberApiResponse<unknown>>(
    api.GET_TEAM_MEMBER_PHONE_NUMBER,
    teamMemberBody(args, user_id)
  );
  if (!res?.success || res.data === null || res.data === undefined) return [];
  return parseTeamMemberPhones(res.data);
}

async function getTeamMemberAddresses(
  args: TeamMemberProfileArgs
): Promise<MyProfileAddressItem[]> {
  const { business_id, device_token, session_id, user_id } = args;
  if (!business_id || !device_token || !session_id || user_id === "") return [];
  const res = await post<TeamMemberApiResponse<unknown>>(
    api.GET_TEAM_MEMBER_ADDRESS,
    teamMemberBody(args, user_id)
  );
  if (!res?.success || res.data === null || res.data === undefined) return [];
  return parseTeamMemberAddresses(res.data);
}

async function getTeamMemberProofs(args: TeamMemberProfileArgs): Promise<MyProfileIdProof[]> {
  const { business_id, device_token, session_id, user_id } = args;
  if (!business_id || !device_token || !session_id || user_id === "") return [];
  const res = await post<TeamMemberApiResponse<unknown>>(
    api.GET_TEAM_MEMBER_PROOFS,
    teamMemberBody(args, user_id)
  );
  if (!res?.success || res.data === null || res.data === undefined) return [];
  return parseTeamMemberProofs(res.data);
}

export function loginActivityFromTeamMemberDetail(
  details: MyProfileUserDetails | null | undefined
): LoginActivityItem[] {
  const um = details?.user_management;
  return Array.isArray(um) ? um : [];
}

export const teamMemberProfileService = {
  getTeamMemberDetail,
  getTeamMemberPhoneNumbers,
  getTeamMemberAddresses,
  getTeamMemberProofs,
  enrichTeamMemberIdProofs,
  mapTeamMemberDetailToUser,
  loginActivityFromTeamMemberDetail,
};
